import { useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@vd/ui';
import { ChevronRight, GraduationCap } from 'lucide-react';
import { keptFor } from './videoRetention';
import styles from './LearnAboutRecordings.module.css';

/**
 * "Learn about video recordings" (Jason, 24 Oct, t-yjcAMB): a box above What
 * gets saved that opens what a walkthrough does and how it works, for someone
 * deciding whether to record one.
 */
export function LearnAboutRecordings({ retentionDays }: { retentionDays: number | undefined }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={styles.box} onClick={() => setOpen(true)} data-testid="learn-recordings">
        <span className={styles.icon} aria-hidden="true"><GraduationCap size={18} /></span>
        <span className={styles.text}>
          <strong>Learn about video recordings</strong>
          <span>What a walkthrough does and how it works</span>
        </span>
        <ChevronRight size={16} aria-hidden="true" className={styles.chevron} />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} width={560}>
        <ModalHeader>Video recordings</ModalHeader>
        <ModalBody>
          <div className={styles.body} data-testid="learn-recordings-modal">
            <p>
              A walkthrough is a recording of your screen while you talk through what is wrong. Show the
              problems as you meet them, and Toado turns each one into its own ticket.
            </p>
            <h3>How it works</h3>
            <ol>
              <li><strong>Start recording.</strong> Chrome asks which tab, window or screen to share, and whether to use your microphone.</li>
              <li><strong>Pin each problem.</strong> Hold Alt and click the thing that is wrong, or use the pin button on the Toado panel, and say what you see.</li>
              <li><strong>Stop.</strong> The recording uploads in the background while you carry on. Each pin gets a short clip, your words transcribed, and the page&apos;s console, network requests and errors from that moment.</li>
              <li><strong>Review and file.</strong> Read each ticket Toado wrote up, change what you like, and file them to your board, or discard the ones you do not want.</li>
            </ol>
            <h3>What to know</h3>
            <ul>
              <li>Recording needs the Toado Chrome extension, connected to your account.</li>
              <li>Nothing reaches your board until you file it. Until then a walkthrough is yours alone.</li>
              <li>Its video is kept {keptFor(retentionDays, 'for as long as your plan keeps walkthrough video')}. The tickets, their transcripts and screenshots stay until they are deleted.</li>
            </ul>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={() => setOpen(false)}>Got it</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
